import { useTranslation } from '@/layers/presentation/i18n/client';
import type { EtlSponsor } from '@/types/etl';
import type { Program, SponsorEASiteSpecificResult, SponsorTrial } from '@/types/models';
import { useMemo } from 'react';

export interface EvidenceItem {
  label: string;
  score: number;
  sources: {
    link: string;
    title?: string;
    statement?: string;
    excerpt?: string;
    conditions?: string;
  }[];
}

export interface EvidenceResult {
  finalScore: number;
  evidence: EvidenceItem[];
  sponsorType: string | undefined;
  typeModifier: number;
}

function addEvidence(
  evidence: EvidenceItem[],
  label: string,
  score: number,
  sources: EvidenceItem['sources'],
) {
  if (sources.length === 0) return;
  evidence.push({ label, score, sources });
}

export function computeEvidence(
  web: SponsorEASiteSpecificResult[],
  trials: SponsorTrial[],
  t: (key: string) => string,
): EvidenceResult {
  const evidence: EvidenceItem[] = [];
  let baseScore = 0;

  const push = (label: string, score: number, sources: EvidenceItem['sources']) => {
    if (sources.length === 0) return;
    addEvidence(evidence, label, score, sources);
    baseScore = Math.max(baseScore, score);
  };

  if (web && web.length > 0) {
    const officialStatements = web
      .filter(
        w =>
          w.is_document_related_to_sponsor &&
          w.has_official_statement_about_eap &&
          w.document_type === 'info_about_expanded_access' &&
          Boolean(w.official_statement) &&
          w.is_confirming_expanded_access &&
          w.is_document_related_to_eap,
      )
      .map(w => ({ link: w.url, title: w.title, statement: w.official_statement }));
    push(t('Official Statements'), 100, officialStatements);

    const webEapSources = web
      .filter(
        w =>
          w.is_document_related_to_sponsor &&
          w.is_document_related_to_eap &&
          w.is_confirming_expanded_access &&
          w.document_type !== 'info_about_expanded_access',
      )
      .map(w => ({ link: w.url, title: w.title, excerpt: w.expanded_access_excerpt }));
    push(t('EAP Related Articles from Sponsor'), 80, webEapSources);
  }

  if (trials && trials.length > 0) {
    const ctEapActiveSources = trials
      .filter(tr => tr.meta?.status === 'AVAILABLE')
      .map(tr => ({ link: tr.link, title: tr.title, conditions: tr.meta?.conditions?.join(', ') }));
    push(t('Active EAPs on ClinicalTrials.gov'), 60, ctEapActiveSources);

    const ctEapPastSources = trials
      .filter(tr => tr.meta?.status !== 'AVAILABLE')
      .map(tr => ({ link: tr.link, title: tr.title, conditions: tr.meta?.conditions?.join(', ') }));
    push(t('Past EAPs on ClinicalTrials.gov'), 40, ctEapPastSources);
  }

  let typeModifier = 0;
  const sponsorType = web[0]?.sponsor_type as string | undefined;
  if (sponsorType === 'institute') typeModifier = -10;
  else if (sponsorType === 'hospital') typeModifier = -20;
  else if (sponsorType === 'doctor' || sponsorType === 'investigator') typeModifier = -30;

  const finalScore = Math.max(0, baseScore + typeModifier);
  return { finalScore, evidence, sponsorType, typeModifier };
}

export function useSponsorExpandedAccess(input: {
  program?: Program;
  etlSponsor?: EtlSponsor;
}): EvidenceResult {
  const { t } = useTranslation();

  return useMemo(() => getSponsorExpandedAccess(input, t), [input, t]);
}

export function getSponsorExpandedAccess(
  input: { program?: Program; etlSponsor?: EtlSponsor },
  t: (key: string) => string,
): EvidenceResult {
  if (input.program) {
    return computeEvidence(
      input.program.sponsor_ea_web || [],
      input.program.sponsor_trials?.results || [],
      t,
    );
  }

  if (input.etlSponsor) {
    const web = input.etlSponsor.analysis?.expanded_access?.model_output?.site_specific_results.map(
      (w: any) => ({ ...w.analysis?.parsed_json, url: w.url, title: w.title }),
    );
    return computeEvidence(
      web || [],
      input.etlSponsor.expanded_access_search?.clinicaltrials_results?.results || [],
      t,
    );
  }
  return { finalScore: 0, evidence: [], sponsorType: undefined, typeModifier: 0 };
}
